/* eslint-disable you-dont-need-momentjs/is-same */
/* eslint-disable you-dont-need-momentjs/no-moment-constructor */
/* eslint-disable no-param-reassign */
import moment from 'moment'

import {
  addDaysToDate,
  addWorkingDays,
  getCountOfWorkingDays,
  getNextWorkingDay,
  getWeekendDays,
  ISO_8601_DATE_FORMAT,
  LAUNCH_TIME_FORMAT,
  WIZARD_DATE_FORMAT,
} from 'utils/dateUtils'
import {
  dateByFormat as dateByFormatValidation,
  dateMinRange as dateMinRangeValidation,
  required as requiredValidation,
  timeInFuture as timeInFutureValidation,
  validateAll,
} from 'utils/validations'

import {
  FULL_YEAR_DAYS,
  getSendingDays,
  MINIMUM_COLLECTION_PERIOD,
  YEAR_WORK_DAYS,
} from '../steps/constants'
import { DEFAULT_COLLECTION_PERIOD } from '../WizardTrainingCampaign/constants'

export const mapSimulationValidatorByName = {
  name: (field) => requiredValidation(field),
  launchDate: (field) => dateByFormatValidation(field, WIZARD_DATE_FORMAT),
  endDate: (field) => dateByFormatValidation(field, WIZARD_DATE_FORMAT),
  closeTime: (field, endDate) =>
    validateAll([
      dateByFormatValidation(field, WIZARD_DATE_FORMAT),
      dateMinRangeValidation(endDate, field, MINIMUM_COLLECTION_PERIOD),
    ]),
  launchTime: (field, launchDate, tz, randomized) =>
    randomized
      ? undefined
      : validateAll([
          requiredValidation(field),
          timeInFutureValidation(new Date(`${launchDate} ${field}`), tz),
        ]),
  sendRemindersUntil: (field) => dateByFormatValidation(field, ISO_8601_DATE_FORMAT),
}

export const stateToProperties = (state) => ({
  maxEmailPerDay: state.campaign.maxEmailPerDay,
  launchDate: state.campaign.launchDate,
  launchTime: state.campaign.launchTime,
  endDate: state.campaign.endDate,
  closeTime: state.campaign.closeTime,
  randomized: state.campaign.randomized,
  sendAllWeek: state.campaign.sendAllWeek,
  participantsCount: state.campaign.participants.meta.count,
  delivery: state.campaign.delivery,
})

// FR-4: default spreads delivery across a ~5-working-day window instead of a single-day blast,
// rounded up to a tidy number. Only used while the admin hasn't manually set the field (FR-2).
export const getDefaultDailyVolume = (participantsCount) => {
  if (!participantsCount) return ''
  const target = Math.max(1, Math.ceil(Math.ceil(participantsCount / 5) / 10) * 10)
  // FR-3: clamped to 1...N -- for small N the rounded-up target can exceed the audience size
  return Math.min(target, participantsCount)
}

export const calculateCampaignLaunchProperties = ({
  values,
  previousValues,
  participantsCount,
  workdays,
  forceCalculation = false,
  isLaunchMethodNewHire = false,
  isEndDateWasManuallyChanged = false,
  isMaxEmailPerDayWasManuallyChanged = false,
}) => {
  const weekendDays = values.sendAllWeek ? [] : getWeekendDays(workdays)

  if (isLaunchMethodNewHire) {
    if (!values.maxEmailPerDay || values.maxEmailPerDay > participantsCount) {
      values.maxEmailPerDay = participantsCount || ''
    } else if (values.maxEmailPerDay < 1) {
      values.maxEmailPerDay = 1
    }
  } else if (isMaxEmailPerDayWasManuallyChanged && values.maxEmailPerDay) {
    // FR-2: admin's manual value is authoritative -- preserve it, just clamp into [1, participantsCount]
    values.maxEmailPerDay = participantsCount
      ? Math.min(Math.max(values.maxEmailPerDay, 1), participantsCount)
      : Math.max(values.maxEmailPerDay, 1)
  } else {
    // FR-4: smart default while the field is untouched
    values.maxEmailPerDay = getDefaultDailyVolume(participantsCount)
  }

  const mNow = moment()
  let mLaunchDate = moment(values.launchDate)
  const mEndDate = moment(values.endDate)
  const isLaunchDateValid = moment(values.launchDate, WIZARD_DATE_FORMAT, true).isValid()
  let isEndDateValid = moment(values.endDate, WIZARD_DATE_FORMAT, true).isValid()

  // determinate single format
  if (isLaunchDateValid) values.launchDate = mLaunchDate.format(WIZARD_DATE_FORMAT)
  if (isEndDateValid) values.endDate = mEndDate.format(WIZARD_DATE_FORMAT)

  values.randomized = values.randomized || false

  const isLaunchToday = mLaunchDate.isSame(mNow, 'day')
  const isLaunchBefore = mLaunchDate.isBefore(mNow)
  const sendAllWeekChanged = values.sendAllWeek !== previousValues.sendAllWeek
  const launchDateChanged = values.launchDate !== previousValues.launchDate

  if (values.launchDate && isLaunchDateValid) {
    if (launchDateChanged && isLaunchBefore && !isLaunchToday) {
      values.launchDate = getNextWorkingDay(mNow.clone(), weekendDays).format(WIZARD_DATE_FORMAT)
      mLaunchDate = moment(values.launchDate)
    } else if (
      (launchDateChanged || sendAllWeekChanged) &&
      weekendDays.includes(mLaunchDate.day())
    ) {
      values.launchDate = getNextWorkingDay(mLaunchDate.clone(), weekendDays).format(
        WIZARD_DATE_FORMAT
      )
      mLaunchDate = moment(values.launchDate)
    }

    const isEndBeforeLaunch = mEndDate.isBefore(mLaunchDate)
    const isEndWeekend = weekendDays.includes(mEndDate.day())
    const isEndSameLaunch = moment(mEndDate.format(WIZARD_DATE_FORMAT)).isSame(
      mLaunchDate.format(WIZARD_DATE_FORMAT)
    )

    if (values.endDate || values.maxEmailPerDay) {
      let endDateHasChanged = values.endDate !== previousValues.endDate
      const launchDateHasChanged = values.launchDate !== previousValues.launchDate
      const launchTimeHasChanged = values.launchTime !== previousValues.launchTime
      const randomizedHasChanged = values.randomized !== previousValues.randomized
      const daysToAdd = weekendDays.length ? YEAR_WORK_DAYS : FULL_YEAR_DAYS

      if (
        (forceCalculation ||
          launchDateHasChanged ||
          launchTimeHasChanged ||
          endDateHasChanged ||
          randomizedHasChanged ||
          sendAllWeekChanged) &&
        isEndDateValid
      ) {
        if (
          forceCalculation ||
          launchDateHasChanged ||
          isEndBeforeLaunch ||
          isEndSameLaunch ||
          isEndWeekend
        ) {
          if (values.maxEmailPerDay) {
            const workingDays = getCountOfWorkingDays(
              mLaunchDate.clone(),
              mEndDate.clone(),
              weekendDays
            )

            // Recalculate endDate from maxEmailPerDay when the window is auto-managed.
            // forceCalculation (participant changes) must be included -- otherwise a 1-day
            // window left over from selecting the first participant stays stuck, and the
            // emails/day overwrite below turns the FR-4 smart default into N (e.g. 27).
            const shouldRecalculateEndDate =
              !isEndDateWasManuallyChanged &&
              (forceCalculation ||
                launchDateHasChanged ||
                isEndBeforeLaunch ||
                isEndWeekend ||
                (isLaunchMethodNewHire && (participantsCount < workingDays || !workingDays)))

            if (shouldRecalculateEndDate) {
              const endDateValue = isLaunchMethodNewHire
                ? addWorkingDays(mLaunchDate.clone(), daysToAdd, weekendDays).format(
                    WIZARD_DATE_FORMAT
                  )
                : addWorkingDays(
                    mLaunchDate.clone(),
                    // launch date itself is sending day 1 (IC-48293), so only (D - 1) days are added
                    Math.max(Math.ceil(participantsCount / values.maxEmailPerDay) - 1, 0),
                    weekendDays
                  ).format(WIZARD_DATE_FORMAT)

              values.endDate = endDateValue
            }
          } else {
            const endDateValue = isLaunchMethodNewHire
              ? addWorkingDays(mLaunchDate.clone(), daysToAdd, weekendDays).format(
                  WIZARD_DATE_FORMAT
                )
              : getNextWorkingDay(mLaunchDate.clone(), weekendDays, false).format(
                  WIZARD_DATE_FORMAT
                )

            values.endDate = endDateValue
          }
        }
        // getSendingDays counts days AFTER launch; launch itself is sending day 1 (IC-48293).
        // Without +1, a same-day campaign (endDate === launchDate) yields 0 and
        // Math.ceil(participants / 0) === Infinity for Emails/day.
        const exclusiveSendingDays = getSendingDays(values.launchDate, values.endDate, weekendDays)
        values.sendingDays = exclusiveSendingDays == null ? null : exclusiveSendingDays + 1

        // Derive emails/day from the delivery window only when the admin changed the end date.
        // On forceCalculation / launch-date flows, maxEmailPerDay (smart default or manual
        // clamp) is authoritative and endDate is derived from it above -- overwriting here
        // from a stale window makes incremental selection disagree with All Company.
        if (endDateHasChanged && values.maxEmailPerDay && values.sendingDays) {
          values.maxEmailPerDay = Math.ceil(participantsCount / values.sendingDays)
        }
      } else if (
        values.maxEmailPerDay &&
        values.maxEmailPerDay !== previousValues.maxEmailPerDay &&
        !isLaunchMethodNewHire
      ) {
        values.sendingDays = Math.ceil(participantsCount / values.maxEmailPerDay)
        values.endDate = addWorkingDays(
          mLaunchDate.clone(),
          // launch date itself is sending day 1 (IC-48293), so only (D - 1) days are added
          Math.max(values.sendingDays - 1, 0),
          weekendDays
        ).format(WIZARD_DATE_FORMAT)
      }

      endDateHasChanged = values.endDate !== previousValues.endDate

      if (endDateHasChanged) {
        values.closeTime = addDaysToDate(values.endDate, DEFAULT_COLLECTION_PERIOD, true)
      }
    }
  }

  if (values.randomized) {
    values.launchTime = '09:00'
  } else if (previousValues.randomized && !values.randomized) {
    values.launchTime = ''
  }

  const isLaunchTimeValid = moment(values.launchTime, LAUNCH_TIME_FORMAT, true).isValid()

  isEndDateValid = moment(values.endDate, WIZARD_DATE_FORMAT, true).isValid()

  if (!isLaunchDateValid || !isEndDateValid || (!values.randomized && !isLaunchTimeValid)) {
    values.sendingDays = null
  }

  return values
}

export const nextPreservePersistedMaxEmailPerDay = (state, nextCount) => {
  const previousCount = state.campaign.participants.meta.count
  const audienceChangedAfterHydration = previousCount > 0 && nextCount !== previousCount

  return !!state.campaign.preservePersistedMaxEmailPerDay && !audienceChangedAfterHydration
}

export const getSimulationCampaignLaunchProperties = (state, propertiesToSwap, forceCalculation) =>
  calculateCampaignLaunchProperties({
    values: { ...stateToProperties(state), ...propertiesToSwap },
    previousValues: stateToProperties(state),
    participantsCount:
      propertiesToSwap.participantsCount != null
        ? propertiesToSwap.participantsCount
        : state.campaign.participants.meta.count,
    workdays: state.workdays,
    forceCalculation,
    isLaunchMethodNewHire: !!state.campaign.triggerBasedCampaignData,
    isEndDateWasManuallyChanged: state.campaign.isEndDateWasManuallyChanged,
    isMaxEmailPerDayWasManuallyChanged:
      state.campaign.isMaxEmailPerDayWasManuallyChanged ||
      state.campaign.preservePersistedMaxEmailPerDay,
  })
